import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router';
import { AppLinkRedirect } from './AppLinkRedirect';
import { TenantProvider, useTenant } from '../lib/tenant';
import { SessionProvider, useSession } from '../lib/session';
import { AppShell } from '../ui/shell';
import { Skeleton } from '../ui/kit';
import { Landing } from '../screens/Landing';
import { TenantNotFound } from '../screens/TenantNotFound';
import { Welcome } from '../screens/Welcome';
import { Home } from '../screens/Home';
import { Signup } from '../screens/Signup';
import { hasCapability, isPhase1 } from '../lib/phase1';
import { UpdateBanner } from '../ui/update';

// Ağır ekranlar ayrı parçada (ilk açılış hızlı kalsın).
const Login = lazy(() => import('../screens/Login'));
const Order = lazy(() => import('../screens/Order'));
const OrderTrack = lazy(() => import('../screens/OrderTrack'));
const Pay = lazy(() => import('../screens/Pay'));
const Rewards = lazy(() => import('../screens/Rewards'));
const Account = lazy(() => import('../screens/Account'));
const Profile = lazy(() => import('../screens/Profile'));
const OrderHistory = lazy(() => import('../screens/OrderHistory'));
const PointHistory = lazy(() => import('../screens/PointHistory'));
const TierJourney = lazy(() => import('../screens/TierJourney'));
const Terms = lazy(() => import('../screens/Terms'));
const Reservations = lazy(() => import('../screens/Reservations'));
const ReservationNew = lazy(() => import('../screens/ReservationNew'));
const ReservationDetail = lazy(() => import('../screens/ReservationDetail'));
const ReceiptClaim = lazy(() => import('../screens/ReceiptClaim'));
// Phase 1: üye sayfası, harcama karekodu, hesap kurtarma.
const Member = lazy(() => import('../screens/Member'));
const SpendCode = lazy(() => import('../screens/SpendCode'));
const Recover = lazy(() => import('../screens/Recover'));
const Claim = lazy(() => import('../screens/Recover').then((m) => ({ default: m.Claim })));

function ScreenFallback() {
  return (
    <div className="space-y-4 px-5 pt-20">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-44 w-full rounded-3xl" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<ScreenFallback />}>{children}</Suspense>;
}

/** /:tenant kökü: işletme + oturum sağlayıcıları. */
function TenantRoot() {
  const { tenant = '' } = useParams();
  const slug = tenant.toLowerCase();
  return (
    <TenantProvider slug={slug} key={slug}>
      <SessionProvider slug={slug}>
        <TenantGate />
      </SessionProvider>
    </TenantProvider>
  );
}

function TenantGate() {
  const { notFound } = useTenant();
  if (notFound) return <TenantNotFound />;
  return <Outlet />;
}

/**
 * Oturum yoksa: Phase 1 işletmede uygulamasız kayıt, eski işletmede karşılama. Oturum varsa: Phase 1'de sade üye kabuğu
 * (alt sekme yok — Wallet kartı esas), eski işletmede sipariş/rezervasyon sekmeli uygulama kabuğu.
 */
function RequireSession() {
  const { token } = useSession();
  const { bootstrap, loading } = useTenant();
  // İlk açılışta (önbellek yok) akış henüz bilinmiyor: eski karşılama/kabuk bir an görünüp kaybolmasın.
  if (!bootstrap && loading) return <div className="card-emerald min-h-dvh" aria-busy />;
  const phase1 = isPhase1(bootstrap);
  if (!token) return phase1 ? <Signup /> : <Welcome />;
  if (phase1) {
    return (
      <div className="min-h-dvh">
        <UpdateBanner />
        <Outlet />
      </div>
    );
  }
  return <AppShell />;
}

/** Phase 1 ana sayfası üye sayfasıdır; eski işletmede mevcut ana ekran. */
function IndexScreen() {
  const { bootstrap } = useTenant();
  return isPhase1(bootstrap) ? <Lazy><Member /></Lazy> : <Home />;
}

/** Modül (capability) kapalıysa ekranı gösterme — üye sayfasına dön (kod silinmez, yalnız gizlenir). */
function Cap({ need, children }: { need: string[]; children: ReactNode }) {
  const { bootstrap } = useTenant();
  if (!need.some((k) => hasCapability(bootstrap, k))) return <Navigate to=".." replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  { path: '/', element: <Landing /> },
  { path: '/k/:tenant/*', element: <AppLinkRedirect /> },
  {
    path: '/:tenant',
    element: <TenantRoot />,
    children: [
      { path: 'giris', element: <Lazy><Login /></Lazy> },
      { path: 'kosullar', element: <Lazy><Terms /></Lazy> },
      { path: 'kurtar', element: <Lazy><Recover /></Lazy> },
      { path: 'claim', element: <Lazy><Claim /></Lazy> },
      {
        element: <RequireSession />,
        children: [
          { index: true, element: <IndexScreen /> },
          { path: 'member', element: <Lazy><Member /></Lazy> },
          { path: 'harcama', element: <Lazy><SpendCode /></Lazy> },
          { path: 'siparis', element: <Cap need={['commerce.ordering', 'commerce.tableOrdering']}><Lazy><Order /></Lazy></Cap> },
          { path: 'siparis/:orderId', element: <Cap need={['commerce.ordering', 'commerce.tableOrdering']}><Lazy><OrderTrack /></Lazy></Cap> },
          { path: 'kod', element: <Lazy><Pay /></Lazy> },
          { path: 'oduller', element: <Lazy><Rewards /></Lazy> },
          { path: 'rezervasyon', element: <Cap need={['reservations']}><Lazy><Reservations /></Lazy></Cap> },
          { path: 'rezervasyon/yeni', element: <Cap need={['reservations']}><Lazy><ReservationNew /></Lazy></Cap> },
          { path: 'rezervasyon/:reservationId', element: <Cap need={['reservations']}><Lazy><ReservationDetail /></Lazy></Cap> },
          { path: 'hesap', element: <Lazy><Account /></Lazy> },
          { path: 'hesap/profil', element: <Lazy><Profile /></Lazy> },
          { path: 'hesap/siparisler', element: <Lazy><OrderHistory /></Lazy> },
          { path: 'hesap/yildizlar', element: <Lazy><PointHistory /></Lazy> },
          { path: 'hesap/seviye', element: <Lazy><TierJourney /></Lazy> },
          { path: 'hesap/fis-ekle', element: <Lazy><ReceiptClaim /></Lazy> },
          { path: '*', element: <Navigate to=".." replace /> },
        ],
      },
    ],
  },
]);
